import React from 'react';
import { FiBarChart2, FiTrendingUp, FiUsers, FiClock, FiAward, FiZap, FiShield } from 'react-icons/fi';

const EducationAnalytics = () => {
  const analyticsAreas = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Enrollment Analytics',
      description: 'Track enrollment patterns across programs, years, and demographics. Understand which programs are growing, which are declining, and why. Use this data to optimize your academic offerings and marketing strategies.',
      stat: 'Institutions using enrollment analytics see 30% higher enrollment growth',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Academic Performance Tracking',
      description: 'Monitor student performance across subjects, programs, and cohorts. Identify struggling students early, understand teaching effectiveness, and continuously improve academic outcomes.',
      stat: '70% of institutions identify at-risk students earlier with performance analytics',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Attendance and Engagement Patterns',
      description: 'Track attendance trends and student engagement. Identify patterns that correlate with academic success or risk. Use real-time data to intervene before small issues become big problems.',
      stat: 'Schools with attendance analytics reduce chronic absenteeism by 40%',
      color: 'bg-amber-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Faculty Performance and Workload',
      description: 'Understand faculty workload distribution, teaching effectiveness, and student feedback patterns. Optimize faculty allocation and identify professional development needs.',
      stat: '75% of institutions improve faculty satisfaction with workload analytics',
      color: 'bg-rose-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Accreditation and Compliance Readiness',
      description: 'Generate comprehensive reports for NAAC, NIRF, and other accreditation bodies automatically. Track compliance metrics in real-time and always be ready for inspections.',
      stat: '90% of institutions reduce accreditation stress with automated reporting',
      color: 'bg-purple-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Institutional Growth and Efficiency',
      description: 'Track key performance indicators across your institution—admission conversion rates, student retention, faculty turnover, and operational costs. Identify growth opportunities and efficiency improvements.',
      stat: 'Data-driven institutions grow 3x faster than intuition-driven ones',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Better Decision-Making',
      description: 'Replace gut feelings with data. Make strategic decisions based on real evidence, not assumptions.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Early Intervention for Students',
      description: 'Identify at-risk students early and provide targeted support. Improve student outcomes and retention.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Optimized Resource Allocation',
      description: 'Understand where resources are most needed. Allocate budget, staff, and infrastructure more effectively.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Improved Institutional Reputation',
      description: 'Use data to demonstrate quality and outcomes. Build trust with parents, students, and accreditation bodies.',
      icon: <FiAward className="w-5 h-5" />
    },
    {
      title: 'Higher Enrollment and Retention',
      description: 'Use insights to attract more students and keep them enrolled. Data-driven strategies improve both enrollment and retention.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Competitive Advantage',
      description: 'Institutions that use data effectively have a significant advantage over those that don\'t. They grow faster and perform better.',
      icon: <FiShield className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For decades, educational decisions in India were made based on intuition, experience, and tradition. Principals relied on their gut feelings. Trustees made decisions based on what "felt right." Administrators followed processes because "that's how it's always been done."
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But gut feeling is not a strategy. <strong className="text-slate-900">In a competitive, fast-changing education landscape, intuition alone is not enough.</strong> Institutions that rely on it are being left behind by those that use data to drive decisions.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Educational analytics is transforming Indian education. Here's how institutions are using data to grow, improve, and succeed.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">3x</div>
          <div className="text-sm text-slate-500">Faster growth with data-driven decisions</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">40%</div>
          <div className="text-sm text-slate-500">Reduction in chronic absenteeism</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">75%</div>
          <div className="text-sm text-slate-500">Improved faculty satisfaction with analytics</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How Analytics Is Transforming Indian Education</h2>

      {/* Analytics Areas */}
      {analyticsAreas.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">{item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Educational Analytics Delivers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Gut Feeling vs. Data-Driven Decisions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Gut Feeling Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Decisions based on intuition</span></li>
              <li className="flex items-start gap-2">• <span>Hard to justify to stakeholders</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent outcomes</span></li>
              <li className="flex items-start gap-2">• <span>Slow to identify problems</span></li>
              <li className="flex items-start gap-2">• <span>Reactive rather than proactive</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Data-Driven Approach with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Decisions based on real evidence</span></li>
              <li className="flex items-start gap-2">• <span>Clear justification for decisions</span></li>
              <li className="flex items-start gap-2">• <span>Consistent, reliable outcomes</span></li>
              <li className="flex items-start gap-2">• <span>Early identification of issues</span></li>
              <li className="flex items-start gap-2">• <span>Proactive intervention and improvement</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Makes Analytics Accessible</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Dashboards</h4>
            <p className="text-xs text-slate-600">See key metrics at a glance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Trend Analysis</h4>
            <p className="text-xs text-slate-600">Track patterns over time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">Predictive Insights</h4>
            <p className="text-xs text-slate-600">Identify risks and opportunities early</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Accreditation Reports</h4>
            <p className="text-xs text-slate-600">Generate NAAC and NIRF reports automatically</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Access</h4>
            <p className="text-xs text-slate-600">View analytics from anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Actionable Insights</h4>
            <p className="text-xs text-slate-600">Turn data into action</p>
          </div>
        </div>
      </div>

      {/* The Data Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Data Journey: From Information to Action</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-200">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-xs">Collect</h4>
            <p className="text-xs text-slate-600">Gather data from all sources</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-xs">Analyze</h4>
            <p className="text-xs text-slate-600">Find patterns and insights</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-xs">Decide</h4>
            <p className="text-xs text-slate-600">Make informed decisions</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-200">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-xs">Grow</h4>
            <p className="text-xs text-slate-600">Drive improvement and growth</p>
          </div>
        </div>
      </div>

      {/* The Human Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Impact of Analytics</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every data point is a student, a teacher, or a family. Analytics isn't just about numbers—it's about helping students succeed, supporting teachers, and building better institutions.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          When institutions use data effectively, they can identify struggling students early and provide support. They can understand what teaching methods work best. They can allocate resources where they're needed most. The result is better outcomes for everyone.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Data isn't cold. It's insight. Insight that helps people succeed.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Make Data-Driven Decisions with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          The days of making decisions based on gut feeling are ending. In 2026, successful institutions use data to drive growth, improve outcomes, and build better futures.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides the analytics you need—real-time dashboards, trend analysis, predictive insights, and accreditation reports. All in one platform. All accessible from anywhere.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for educational analytics.</strong> From gut feeling to data-driven decisions. Drive growth with confidence.
        </p>
      </div>
    </>
  );
};

export default EducationAnalytics;